import { useNavigate } from 'react-router-dom'
import { Alert, Button } from '@kcm/ui'
import { useTranslate } from '@tolgee/react'
import type { KycState, KycStatusResponse } from '@kcm/contracts'
import { useSite } from './SiteProvider'

type Reason = NonNullable<KycStatusResponse['reason']>
/**
 * Every terminal-ish verification outcome, in one place (PLAN.md §11.4).
 *
 * The legacy funnel handled exactly one: approved. A decline, a manual review
 * or an expiry all left the user on a spinner, which is a support ticket at
 * best and an abandoned application otherwise.
 *
 * Shared by the step-2 screen and the Didit return page, because both can
 * arrive at any of these and they must say the same thing.
 */
export function KycOutcome({
  state,
  reason,
}: {
  state: KycState
  reason?: Reason | undefined
}) {
  const navigate = useNavigate()
  const site = useSite()
  const { t } = useTranslate()

  if (state === 'approved') {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="success" title={t('kyc.approvedTitle')}>
          {t('kyc.approvedBody')}
        </Alert>
        <Button onClick={() => navigate('/confirm', { replace: true })}>{t('common.continue')}</Button>
      </div>
    )
  }

  if (state === 'in_review') {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="info" title={t('kyc.reviewTitle')}>
          <p>{t('kyc.reviewBody')}</p>
        </Alert>
        <Button onClick={() => navigate('/confirm', { replace: true })}>{t('common.continue')}</Button>
        <p className="text-sm text-muted">
          {t('kyc.questions')} <a className="underline" href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>
        </p>
      </div>
    )
  }

  if (state === 'declined') {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="error" title={t('kyc.declinedTitle')}>
          <p>{t(`decline.${reason ?? 'other'}`)}</p>
        </Alert>
        {/*
          Still no self-service retry: a decline can be a genuine fraud signal,
          and letting someone loop until a document passes is exactly the hole
          that would create. Continuing is not retrying — the rest of the
          application is worth having on file when support looks at this, and
          the CRM has already been told the verification failed.
        */}
        <Button onClick={() => navigate('/confirm', { replace: true })}>
          {t('kyc.continueAnyway')}
        </Button>
        <p className="text-sm text-muted">{t('kyc.finishLaterBody')}</p>
        <p className="text-sm text-muted">
          {t('kyc.contactPrefix')}{' '}
          <a className="underline" href={`mailto:${site.supportEmail}`}>
            {site.supportEmail}
          </a>{' '}
          {t('kyc.contactSuffix')}
        </p>
      </div>
    )
  }

  // expired — the one case where starting over is genuinely the fix, so it
  // leads, but it is not the only way out: a session that keeps expiring must
  // not become the reason an application is never finished.
  return (
    <div className="flex flex-col gap-5">
      <Alert tone="info" title={t('kyc.expiredTitle')}>
        {t('kyc.expiredBody')}
      </Alert>
      <Button onClick={() => navigate('/identity', { replace: true })}>{t('common.tryAgain')}</Button>
      <Button variant="ghost" onClick={() => navigate('/confirm', { replace: true })}>
        {t('kyc.continueAnyway')}
      </Button>
      <p className="text-sm text-muted">{t('kyc.finishLaterBody')}</p>
    </div>
  )
}
